import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or retrieve session ID
const getSessionId = () => {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
        sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
};

// Detect device type
const getDeviceType = () => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
};

// Detect browser
const getBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Edg')) return 'Edge';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Opera') || userAgent.includes('OPR')) return 'Opera';
    return 'Other';
};

export function useAnalyticsTracking(docId) {
    const startTimeRef = useRef(Date.now());
    const maxScrollDepthRef = useRef(0);
    const lastSentRef = useRef({ readTime: 0, scrollDepth: 0 });
    const intervalRef = useRef(null);

    // Track scroll depth
    const handleScroll = useCallback(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        const scrollPercentage = docHeight > 0 ? Math.round((scrollTop / docHeight) * 100) : 0;
        maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, Math.min(scrollPercentage, 100));
    }, []);

    // Send analytics data
    const sendAnalytics = useCallback(async () => {
        if (!docId) return;

        const readTime = Math.round((Date.now() - startTimeRef.current) / 1000);
        const scrollDepth = maxScrollDepthRef.current;

        // Only send if there's meaningful change
        if (readTime === lastSentRef.current.readTime && scrollDepth === lastSentRef.current.scrollDepth) {
            return;
        }

        lastSentRef.current = { readTime, scrollDepth };

        try {
            await axios.post(`${API}/analytics/track`, null, {
                params: {
                    doc_id: docId,
                    session_id: getSessionId(),
                    read_time: readTime,
                    scroll_depth: scrollDepth,
                    device_type: getDeviceType(),
                    browser: getBrowser()
                }
            });
        } catch (error) {
            // Silent fail - analytics shouldn't break the page
            console.debug('Analytics tracking failed:', error);
        }
    }, [docId]);

    useEffect(() => {
        if (!docId) return;

        // Reset tracking for new doc
        startTimeRef.current = Date.now();
        maxScrollDepthRef.current = 0;
        lastSentRef.current = { readTime: 0, scrollDepth: 0 };

        // Add scroll listener
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Send analytics every 30 seconds
        intervalRef.current = setInterval(sendAnalytics, 30000);

        // Send on visibility change (when user leaves tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                sendAnalytics();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Send on unload
        const handleUnload = () => {
            sendAnalytics();
        };
        window.addEventListener('beforeunload', handleUnload);

        // Initial send after 10 seconds
        const initialTimeout = setTimeout(sendAnalytics, 10000);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleUnload);
            if (intervalRef.current) clearInterval(intervalRef.current);
            clearTimeout(initialTimeout);
            // Final send on cleanup
            sendAnalytics();
        };
    }, [docId, handleScroll, sendAnalytics]);

    return {
        getReadTime: () => Math.round((Date.now() - startTimeRef.current) / 1000),
        getScrollDepth: () => maxScrollDepthRef.current
    };
}

export default useAnalyticsTracking;
